/* =========================================================
   AF — All-Reviews submission form (product picker + submit)
   File: assets/af-all-reviews-form.js

   - Product picker: Shopify predictive search scoped to products only
     (this store errors when product is combined with other resource types).
     Sends the BARE NUMERIC product_id — never the GID (that writes the review
     to the wrong product). Resolves via /products/{handle}.js when the
     suggest payload lacks a clean numeric id.
   - Submit blocked until a product is chosen from the results.
   - POST /apps/store/reviews. Branch on `ok === true`, not HTTP status.
   - Optimistic pending card + "Review another one" reset.

   Self-contained IIFE with a per-instance init guard (house AF pattern).
   ========================================================= */
(() => {
  "use strict";

  const PROXY_URL = "/apps/store/reviews";
  const DEBOUNCE_MS = 220;
  // Pending reviews persist on THIS device until approved (like the wishlist), so a
  // second submit or a refresh never makes a reviewer's pending card vanish.
  const PENDING_KEY = "af_arvf_pending_v1";
  const PENDING_MAX_AGE_MS = 60 * 24 * 60 * 60 * 1000; // 60 days — self-cleans stale entries

  const bareId = (v) => {
    const s = String(v == null ? "" : v).trim();
    if (/^\d+$/.test(s)) return s; // already a clean numeric id
    const m = s.match(/(\d+)(?!.*\d)/); // trailing digits of a GID, as a fallback
    return m ? m[1] : "";
  };

  const firstName = (name) => {
    const n = ((name || "") + "").trim();
    return n ? n.split(/\s+/)[0] : n;
  };

  // Decode HTML entities (the review API returns encoded text like "&amp;").
  // textarea is RCDATA, so tags are never parsed as elements → XSS-safe.
  const decodeEntities = (s) => {
    const str = (s || "") + "";
    if (str.indexOf("&") === -1) return str;
    const ta = document.createElement("textarea");
    ta.innerHTML = str;
    return ta.value;
  };

  const initOne = (root) => {
    if (!root || root.dataset.afArvfInit === "1") return;
    root.dataset.afArvfInit = "1";

    const q = (sel) => root.querySelector(sel);
    const picker = q("[data-arvf-picker]");
    const searchWrap = q("[data-arvf-search-wrap]");
    const searchInput = q("[data-arvf-search]");
    const resultsEl = q("[data-arvf-results]");
    const statusEl = q("[data-arvf-picker-status]");
    const selectedEl = q("[data-arvf-selected]");
    const selImg = q("[data-arvf-selected-img]");
    const selTitle = q("[data-arvf-selected-title]");
    const selVendor = q("[data-arvf-selected-vendor]");
    const changeBtn = q("[data-arvf-change]");
    const form = q("[data-arvf-form]");
    const submitBtn = q("[data-arvf-submit]");
    const msgEl = q("[data-arvf-msg]");
    const successEl = q("[data-arvf-success]");
    const successList = q("[data-arvf-success-list]");
    const pendingCol = q("[data-arvf-pending-col]");
    const scoreEl = q("[data-arvf-score]");
    if (!picker || !searchInput || !resultsEl || !form) return;

    const predictiveBase = (root.dataset.predictiveUrl || "/search/suggest").replace(/\.json$/, "");
    const suggestUrl = predictiveBase + ".json";

    let selected = null; // { id, handle, title, image, vendor }
    let activeIndex = -1;
    let currentResults = [];
    let debounceT = 0;
    let ctrl = null;

    /* ---------- messages ---------- */
    const showMsg = (text, type) => {
      if (!msgEl) return;
      msgEl.textContent = text || "";
      msgEl.hidden = !text;
      msgEl.className = "af-rv__msg" + (type ? " af-rv__msg--" + type : "");
    };
    const setStatus = (text) => {
      if (!statusEl) return;
      statusEl.textContent = text || "";
      statusEl.hidden = !text;
    };

    /* ---------- results dropdown ---------- */
    const closeResults = () => {
      resultsEl.classList.remove("is-open");
      resultsEl.hidden = true;
      resultsEl.innerHTML = "";
      searchInput.setAttribute("aria-expanded", "false");
      searchInput.removeAttribute("aria-activedescendant");
      activeIndex = -1;
      currentResults = [];
    };

    const renderResults = (products) => {
      currentResults = products;
      activeIndex = -1;
      resultsEl.innerHTML = "";
      if (!products.length) {
        closeResults();
        setStatus("No products found. Try another spelling.");
        return;
      }
      setStatus("");
      const frag = document.createDocumentFragment();
      products.forEach((p, i) => {
        const li = document.createElement("li");
        li.setAttribute("role", "option");
        li.id = root.id + "-opt-" + i;

        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "af-arvf__result";
        btn.dataset.index = String(i);

        const img = document.createElement("img");
        img.className = "af-arvf__result-img";
        img.alt = "";
        img.loading = "lazy";
        img.decoding = "async";
        img.width = 44;
        img.height = 44;
        if (p.image) img.src = p.image;
        btn.appendChild(img);

        const meta = document.createElement("div");
        meta.className = "af-arvf__result-meta";
        const t = document.createElement("div");
        t.className = "af-arvf__result-title";
        t.textContent = p.title || p.handle || "";
        meta.appendChild(t);
        if (p.vendor) {
          const v = document.createElement("div");
          v.className = "af-arvf__result-vendor";
          v.textContent = p.vendor;
          meta.appendChild(v);
        }
        btn.appendChild(meta);

        btn.addEventListener("click", () => selectProduct(p));
        li.appendChild(btn);
        frag.appendChild(li);
      });
      resultsEl.appendChild(frag);
      resultsEl.hidden = false;
      requestAnimationFrame(() => resultsEl.classList.add("is-open"));
      searchInput.setAttribute("aria-expanded", "true");
    };

    const setActive = (idx) => {
      const items = resultsEl.querySelectorAll(".af-arvf__result");
      if (!items.length) return;
      activeIndex = (idx + items.length) % items.length;
      items.forEach((el, i) => el.classList.toggle("is-active", i === activeIndex));
      const active = items[activeIndex];
      if (active) {
        searchInput.setAttribute("aria-activedescendant", active.parentNode.id);
        active.scrollIntoView({ block: "nearest" });
      }
    };

    /* ---------- predictive search fetch ---------- */
    const runSearch = (term) => {
      if (ctrl) ctrl.abort();
      ctrl = new AbortController();
      const url =
        suggestUrl +
        "?q=" + encodeURIComponent(term) +
        "&resources[type]=product&resources[limit]=6" +
        // Reviews are for products people already own — a sold-out item they bought
        // must still be selectable, and its reviews pay off when it's restocked.
        // The store's Search & Discovery default hides/deprioritizes unavailable
        // products; force them to show for this picker only.
        "&resources[options][unavailable_products]=show";
      fetch(url, { signal: ctrl.signal, headers: { Accept: "application/json" } })
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
        .then((data) => {
          const products =
            (((data || {}).resources || {}).results || {}).products || [];
          renderResults(
            products.map((p) => ({
              id: p.id,
              handle: p.handle,
              title: p.title,
              image: p.image || p.featured_image || "",
              vendor: p.vendor || "",
            }))
          );
        })
        .catch((err) => {
          if (err && err.name === "AbortError") return;
          closeResults();
          setStatus("Couldn't load suggestions. Please try again.");
        });
    };

    searchInput.addEventListener("input", () => {
      const term = searchInput.value.trim();
      window.clearTimeout(debounceT);
      if (term.length < 2) {
        closeResults();
        setStatus("");
        return;
      }
      setStatus("Searching…");
      debounceT = window.setTimeout(() => runSearch(term), DEBOUNCE_MS);
    });

    searchInput.addEventListener("keydown", (e) => {
      if (resultsEl.hidden) return;
      if (e.key === "ArrowDown") { e.preventDefault(); setActive(activeIndex + 1); }
      else if (e.key === "ArrowUp") { e.preventDefault(); setActive(activeIndex - 1); }
      else if (e.key === "Enter") {
        if (activeIndex >= 0 && currentResults[activeIndex]) {
          e.preventDefault();
          selectProduct(currentResults[activeIndex]);
        }
      } else if (e.key === "Escape") {
        closeResults();
      }
    });

    document.addEventListener("click", (e) => {
      if (!picker.contains(e.target)) closeResults();
    });

    /* ---------- selection (resolves the BARE NUMERIC id) ---------- */
    const applySelection = (sel) => {
      selected = sel;
      if (selImg) {
        if (sel.image) { selImg.src = sel.image; selImg.hidden = false; }
        else { selImg.removeAttribute("src"); }
      }
      if (selTitle) selTitle.textContent = sel.title || sel.handle || "";
      if (selVendor) {
        selVendor.textContent = sel.vendor || "";
        selVendor.hidden = !sel.vendor;
      }
      selectedEl.hidden = false;
      searchInput.value = "";
      closeResults();
      setStatus("");
      // Hide the whole search wrapper (input + the magnifier icon), not just the
      // input — otherwise the icon lingers behind the selected chip and looks odd.
      if (searchWrap) searchWrap.hidden = true;
      else searchInput.hidden = true;
      form.hidden = false;
      requestAnimationFrame(() => form.classList.add("is-in"));
      if (submitBtn) submitBtn.disabled = false;
      showMsg("", "");
      // Dismiss the keyboard the search opened. On touch we do NOT auto-focus a
      // field — that reopens the keyboard and buries the just-revealed form under
      // it. Bring the form into view instead.
      try { searchInput.blur(); if (document.activeElement && document.activeElement.blur) document.activeElement.blur(); } catch (e) {}
      const coarse = window.matchMedia && window.matchMedia("(pointer: coarse)").matches;
      if (!coarse) {
        const nameInput = form.querySelector('[name="name"]');
        // Title is optional and hidden behind a toggle now — never auto-focus it.
        if (nameInput && !nameInput.readOnly) nameInput.focus();
      }
      requestAnimationFrame(() => {
        try { (selectedEl || form).scrollIntoView({ behavior: "smooth", block: "nearest" }); } catch (e) {}
      });
    };

    const selectProduct = (p) => {
      const handle = (p.handle || "").trim();
      const clean = bareId(p.id);
      // Prefer a clean numeric id from suggest; otherwise resolve it authoritatively.
      if (clean) {
        applySelection({ id: clean, handle: handle, title: p.title, image: p.image, vendor: p.vendor });
        return;
      }
      if (!handle) {
        setStatus("Couldn't identify that product. Pick another.");
        return;
      }
      setStatus("Confirming product…");
      fetch("/products/" + encodeURIComponent(handle) + ".js", { headers: { Accept: "application/json" } })
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
        .then((prod) => {
          const id = bareId(prod && prod.id);
          if (!id) throw new Error("no id");
          applySelection({
            id: id,
            handle: handle,
            title: (prod && prod.title) || p.title,
            image: p.image || (prod && prod.featured_image) || "",
            vendor: (prod && prod.vendor) || p.vendor || "",
          });
        })
        .catch(() => setStatus("Couldn't confirm that product. Pick another."));
    };

    /* ---------- change product ---------- */
    if (changeBtn) {
      changeBtn.addEventListener("click", () => {
        selected = null;
        selectedEl.hidden = true;
        if (searchWrap) searchWrap.hidden = false;
        else searchInput.hidden = false;
        form.classList.remove("is-in");
        form.hidden = true; // keep the user's typed review; just re-pick
        if (submitBtn) submitBtn.disabled = true;
        setStatus("");
        searchInput.focus();
      });
    }

    /* ---------- optional written-review toggle ---------- */
    const addTextBtn = q("[data-arvf-addtext]");
    const optionalWrap = q("[data-arvf-optional]");
    if (addTextBtn && optionalWrap) {
      addTextBtn.addEventListener("click", () => {
        const opening = optionalWrap.hidden;
        optionalWrap.hidden = !opening;
        addTextBtn.setAttribute("aria-expanded", String(opening));
        addTextBtn.hidden = opening;
        if (opening) { const t = form.querySelector('[name="title"]'); if (t) t.focus(); }
      });
    }

    /* ---------- optimistic pending card (DOM-built; never innerHTML user text) ---------- */
    const starSvg = (filled) =>
      '<svg class="af-rv__star' + (filled ? " af-rv__star--filled" : "") +
      '" viewBox="0 0 20 20" aria-hidden="true"><path d="M10 1.3l2.4 5.3 5.7.7-4.2 3.9 1.1 5.6L10 14l-4.9 2.8 1-5.6L2 7.3l5.7-.7z"/></svg>';

    const buildPendingCard = (review) => {
      const rating = Math.max(0, Math.min(5, parseInt(review.rating, 10) || 0));
      const name = decodeEntities(review.name).trim() || "You";
      const created = review.created_at || new Date().toISOString();

      const art = document.createElement("article");
      art.className = "af-rv__item af-rv__item--pending";

      const badge = document.createElement("div");
      badge.className = "af-rv__item-badge af-rv__item-badge--pending";
      badge.textContent = "Pending";
      art.appendChild(badge);

      const head = document.createElement("div");
      head.className = "af-rv__item-head";
      const avatar = document.createElement("div");
      avatar.className = "af-rv__avatar";
      avatar.setAttribute("aria-hidden", "true");
      avatar.textContent = name.charAt(0).toUpperCase();
      head.appendChild(avatar);

      const idmeta = document.createElement("div");
      idmeta.className = "af-rv__item-idmeta";
      const author = document.createElement("div");
      author.className = "af-rv__item-author";
      author.textContent = name;
      idmeta.appendChild(author);

      const sub = document.createElement("div");
      sub.className = "af-rv__item-sub";
      const stars = document.createElement("div");
      stars.className = "af-rv__item-stars";
      let sh = "";
      for (let s = 1; s <= 5; s++) sh += starSvg(s <= rating);
      stars.innerHTML = sh;
      sub.appendChild(stars);
      const time = document.createElement("time");
      time.className = "af-rv__date";
      time.setAttribute("datetime", created);
      try {
        time.textContent = new Date(created).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "2-digit" });
      } catch (e) { time.textContent = created; }
      sub.appendChild(time);
      idmeta.appendChild(sub);
      head.appendChild(idmeta);
      art.appendChild(head);

      const tText = decodeEntities(review.title).trim();
      if (tText) {
        const title = document.createElement("h3");
        title.className = "af-rv__item-title";
        title.textContent = tText;
        art.appendChild(title);
      }

      const bText = decodeEntities(review.body).trim();
      if (bText) {
        const body = document.createElement("p");
        body.className = "af-rv__item-body";
        body.textContent = bText;
        art.appendChild(body);
      }

      return art;
    };

    /* ---------- pending store (localStorage, per device) ---------- */
    const normBody = (s) => decodeEntities(s || "").trim().toLowerCase().replace(/\s+/g, " ");
    const uid = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
    const savePending = (arr) => { try { localStorage.setItem(PENDING_KEY, JSON.stringify(arr)); } catch (e) {} };
    const loadPending = () => {
      let arr = [];
      try { arr = JSON.parse(localStorage.getItem(PENDING_KEY) || "[]"); } catch (e) { arr = []; }
      if (!Array.isArray(arr)) arr = [];
      const now = Date.now();
      const kept = arr.filter((p) => p && (!p.ts || now - p.ts < PENDING_MAX_AGE_MS));
      if (kept.length !== arr.length) savePending(kept);
      return kept;
    };
    const entryFrom = (review) => ({
      uid: uid(),
      // The backend review id (from the submit response). Lets pruneByStatus ask
      // the status endpoint whether this exact review was approved/rejected/deleted
      // and drop the card accordingly. Empty for honeypot/legacy entries.
      review_id: (review && review.id) || "",
      ts: Date.now(),
      product_id: (selected && selected.id) || review.product_id || "",
      product_handle: (selected && selected.handle) || review.product_handle || "",
      product_title: (selected && selected.title) || review.product_title || "",
      name: review.name || "",
      rating: review.rating || "",
      title: review.title || "",
      body: review.body || "",
      created_at: review.created_at || new Date().toISOString(),
    });

    // Render the pending list into the RIGHT column; show/hide that column (and
    // switch the layout to two columns) based on whether there are any pending.
    const renderPanel = () => {
      const arr = loadPending();
      if (successList) {
        successList.innerHTML = "";
        arr.slice().sort((a, b) => (b.ts || 0) - (a.ts || 0)).forEach((p) => successList.appendChild(buildPendingCard(p)));
      }
      const has = arr.length > 0;
      if (pendingCol) pendingCol.hidden = !has;
      root.classList.toggle("af-arvf--has-pending", has);
      return arr;
    };

    // After a submit, reset the composer to "pick a product" (the form stays open).
    const resetComposer = () => {
      try { form.reset(); } catch (e) {}
      selected = null;
      if (selectedEl) selectedEl.hidden = true;
      if (searchWrap) searchWrap.hidden = false;
      form.classList.remove("is-in");
      form.hidden = true;
      if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = "Submit Review"; }
      searchInput.value = "";
      setStatus("");
      if (scoreEl) scoreEl.textContent = "– / 5";
      if (optionalWrap) optionalWrap.hidden = true;
      if (addTextBtn) { addTextBtn.hidden = false; addTextBtn.setAttribute("aria-expanded", "false"); }
    };

    // Once a pending review shows up approved in the sitewide feed, drop it locally
    // (it now lives in the slider) so the reviewer isn't shown a stale "pending" card.
    const pruneApproved = () => {
      const arr = loadPending();
      if (!arr.length) return;
      fetch("/apps/store/reviews/all?limit=50", { headers: { Accept: "application/json" } })
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
        .then((data) => {
          const approved = data && data.ok && Array.isArray(data.reviews) ? data.reviews : [];
          if (!approved.length) return;
          const key = (h, b) => (h || "").trim().toLowerCase() + "|" + normBody(b);
          const approvedKeys = new Set(approved.map((r) => key(r.product_handle, r.body)));
          const kept = arr.filter((p) => !approvedKeys.has(key(p.product_handle, p.body)));
          if (kept.length === arr.length) return;
          savePending(kept);
          renderPanel(); // updates the right column + collapses it if now empty
        })
        .catch(() => {});
    };

    // Precise prune by backend status: ask /reviews/status about our stored review
    // ids and drop any card that is no longer pending — approved (now in the feed),
    // rejected (soft-deleted by staff), or gone (hard-deleted / unknown). This is
    // what clears a REJECTED card, which never appears in the approved feed so
    // pruneApproved() alone can't catch it. Only entries that carry a review_id are
    // checked; legacy/honeypot entries fall back to pruneApproved + the 60-day TTL.
    // Fails closed: if the endpoint isn't deployed yet (404) or errors, nothing is
    // removed, so shipping this before the backend is a safe no-op.
    const pruneByStatus = () => {
      const withId = loadPending().filter((p) => p && p.review_id);
      if (!withId.length) return;
      const ids = withId.map((p) => p.review_id).slice(0, 50);
      fetch("/apps/store/reviews/status?ids=" + encodeURIComponent(ids.join(",")), { headers: { Accept: "application/json" } })
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
        .then((data) => {
          const statuses = data && data.ok && data.statuses && typeof data.statuses === "object" ? data.statuses : null;
          if (!statuses) return;
          const cur = loadPending(); // re-read: pruneApproved may have run concurrently
          const kept = cur.filter((p) => {
            const st = p && p.review_id ? statuses[p.review_id] : undefined;
            // Drop only on a definitive terminal status; keep on "pending",
            // unknown, or missing (never remove a card we're unsure about).
            return !(st === "approved" || st === "rejected" || st === "gone");
          });
          if (kept.length === cur.length) return;
          savePending(kept);
          renderPanel();
        })
        .catch(() => {});
    };

    /* ---------- submit ---------- */
    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      if (!selected || !selected.id) {
        showMsg("Please choose the product you're reviewing first.", "error");
        return;
      }
      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }
      const btn = submitBtn;
      if (btn) { btn.disabled = true; btn.textContent = "Submitting…"; }
      showMsg("", "");

      const ratingEl = form.querySelector('[name="rating"]:checked');
      const data = {
        product_id: selected.id, // bare numeric — never the GID
        product_handle: selected.handle,
        name: form.querySelector('[name="name"]').value.trim(),
        email: form.querySelector('[name="email"]').value.trim(),
        rating: ratingEl ? ratingEl.value : "",
        title: form.querySelector('[name="title"]').value.trim(),
        body: form.querySelector('[name="body"]').value.trim(),
        website: form.querySelector('[name="website"]').value,
      };

      try {
        const res = await fetch(PROXY_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        });
        let result = {};
        try { result = await res.json(); } catch (e) { result = {}; }

        // Branch on ok === true (not HTTP status, no `success` field).
        // 200 {ok:true, review} = success; 200 {ok:true} no review = honeypot,
        // still treated as success so the bot learns nothing.
        if (res.ok && result.ok === true) {
          // Honeypot submits return ok:true with no review — confirm to fool the
          // bot, but never persist their entry.
          if (!data.website) {
            const arr = loadPending();
            arr.push(entryFrom(result.review || data));
            savePending(arr);
          }
          renderPanel();      // add the new card to the right column
          resetComposer();    // form stays open, ready for another review
          setStatus("Thanks! Your review is saved and now pending. See it under your reviews.");
          if (pendingCol && window.matchMedia && window.matchMedia("(max-width: 989px)").matches) {
            requestAnimationFrame(() => { try { pendingCol.scrollIntoView({ behavior: "smooth", block: "start" }); } catch (e) {} });
          }
          return;
        }
        // 400/429 {ok:false, error} = show verbatim; 401 {detail} no error key
        // = never blank, fall back to a generic line.
        showMsg(result.error || "Something went wrong. Please try again.", "error");
      } catch (err) {
        showMsg("Network error. Please try again.", "error");
      }
      if (btn) { btn.disabled = false; btn.textContent = "Submit Review"; }
    });

    /* ---------- live rating score ("N / 5") ---------- */
    if (scoreEl) {
      form.addEventListener("change", (e) => {
        if (e.target && e.target.name === "rating") scoreEl.textContent = (parseInt(e.target.value, 10) || 0) + " / 5";
      });
    }

    /* ---------- on load: restore this device's pending reviews into the right column ---------- */
    renderPanel();
    pruneByStatus(); // precise: clears approved/rejected/gone cards (incl. rejections)
    pruneApproved(); // fallback: body-match against the approved feed for legacy cards
  };

  const initAll = () => {
    document.querySelectorAll("[data-af-arvf]").forEach(initOne);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll, { once: true });
  } else {
    initAll();
  }
  document.addEventListener("shopify:section:load", initAll);
})();
